import {colorEqual, GlobalRenderConstants, SceneNodes} from "@cm/template-nodes"
import {mathIsEqual, setThreeObjectPart, ThreeObject, updateTransform} from "@template-editor/helpers/three-object"
import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {DisplayMode, ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {ThreeStandHelper} from "@template-editor/helpers/three-stand-helper"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {configThreeHelperObjectLayers, HELPER_OBJECTS_LAYER, MIN_GIZMO_RADIUS, OVERLAY_OBJECTS_LAYER} from "@template-editor/helpers/helper-objects"
import {getLtcTextures, ltcEvaluateDisk, ltcUv} from "@template-editor/helpers/ltc-disk-light"
import {clampColor01} from "@template-editor/helpers/three-utils"

export class SphereLight extends THREE.PointLight {
    radius = 0
}

class SphereLightNode extends THREE.PointLightNode {
    constructor(public override light: SphereLight | null) {
        super(light)
    }

    private readonly radiusNode = TSL.uniform(0)

    override update(frame: THREE.NodeFrame): boolean | undefined {
        const result = super.update(frame)
        this.radiusNode.value = this.light?.radius ?? 0
        return result
    }

    override setupDirect(builder: THREE.NodeBuilder) {
        const lightVector = this.getLightVector(builder) as THREE.Node<"vec3">
        const lightDistance = lightVector.length()
        const colorNode = (this as unknown as {colorNode: THREE.Node<"vec3">}).colorNode

        // radiance = colorNode / πr² so that radiance × formFactor matches the point-light irradiance colorNode / d².
        const radius = this.radiusNode.max(0.001)
        const radiance = colorNode.div(radius.mul(radius).mul(Math.PI))

        const centerDir = lightVector.div(lightDistance)
        const center = TSL.positionView.add(lightVector)
        // a facing disk of radius r subtends less solid angle than the sphere (up to ~3.4× at contact);
        // r·d/√(d²−r²) restores the sphere's solid angle and saturates to the full hemisphere for points
        // on or inside the sphere. The radiance keeps the nominal r — the sphere's surface radiance.
        const diskRadius = radius
            .mul(lightDistance)
            .div(lightDistance.mul(lightDistance).sub(radius.mul(radius)).max(1e-4).sqrt())
            .toVar()
        const ref = centerDir.y
            .abs()
            .lessThan(0.999)
            .select(TSL.vec3(0, 1, 0), TSL.vec3(1, 0, 0))
        // axisV must wind the disk CCW as seen from the point; the reversed cross flips the form-factor sign.
        const axisU = ref.cross(centerDir).normalize().mul(diskRadius)
        const axisV = axisU.div(diskRadius).cross(centerDir).mul(diskRadius)

        const {ltc1, ltc2} = getLtcTextures(builder)
        const uv = ltcUv(TSL.normalView, TSL.positionViewDirection, TSL.roughness)
        const t1 = ltc1.sample(uv)
        const t2 = ltc2.sample(uv)
        const mInv = TSL.mat3(TSL.vec3(t1.x, 0, t1.y), TSL.vec3(0, 1, 0), TSL.vec3(t1.z, 0, t1.w))

        // LTC Fresnel (Hill): f0·t2.x + (f90 − f0)·t2.y
        const f0 = TSL.property("color", "SpecularColorBlended") as unknown as THREE.Node<"vec3">
        const fresnel = f0.mul(t2.x).add(TSL.specularF90.sub(f0).mul(t2.y))
        const diffuseContribution = TSL.property("vec3", "DiffuseContribution") as unknown as THREE.Node<"vec3">

        const identity = TSL.mat3(1, 0, 0, 0, 1, 0, 0, 0, 1)
        // specular needs a fine polygon to look round; the diffuse pool is smooth, so few segments suffice
        const ltcSpecular = radiance.mul(fresnel).mul(ltcEvaluateDisk(mInv, center, axisU, axisV, 32))
        const ltcDiffuse = radiance.mul(diffuseContribution).mul(ltcEvaluateDisk(identity, center, axisU, axisV, 8))

        return {lightColor: radiance, lightDirection: centerDir, ltcDiffuse, ltcSpecular}
    }
}

export class ThreePointLight extends ThreeObject<SceneNodes.PointLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private threeLight: SphereLight = new SphereLight(0xffffff, 1)
    private pointLightHelper: ThreePointLightHelper

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        this.pointLightHelper = new ThreePointLightHelper(this, threeSceneManagerService, onAsyncUpdate)

        const renderer = threeSceneManagerService.$renderer()
        if (!renderer) throw new Error("ThreePointLight: renderer not available")
        if (!renderer.library.lightNodes.has(SphereLight)) {
            renderer.library.addLight(SphereLightNode, SphereLight)
        }

        this.renderObject.add(this.threeLight)
        this.renderObject.add(this.pointLightHelper.getRenderObject())

        this.onDisplayModeChange(threeSceneManagerService.$displayMode())
    }

    override onDisplayModeChange(displayMode: DisplayMode) {
        this.pointLightHelper.getRenderObject().visible = displayMode === "editor"
    }

    override setup(sceneNode: SceneNodes.PointLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["on", "color", "intensity", "radius"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({on, color, intensity, radius}) => {
                    this.threeLight.visible = on
                    this.threeLight.color.setRGB(...color)
                    this.threeLight.intensity = GlobalRenderConstants.lightIntensityScale * intensity * Math.PI * radius * radius
                    this.threeLight.castShadow = true
                    this.threeLight.radius = radius
                },
            ),

            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.threeLight)
                },
            ),

            this.pointLightHelper.update(sceneNode),
        ])
    }

    override dispose() {
        this.pointLightHelper.dispose()
        this.threeLight.dispose()
    }
}

const fibonacciSphereDirections = (count: number) => {
    const goldenAngle = Math.PI * (3 - Math.sqrt(5))
    const directions: THREE.Vector3[] = []
    for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2
        const r = Math.sqrt(Math.max(0, 1 - y * y))
        const theta = i * goldenAngle
        directions.push(new THREE.Vector3(Math.cos(theta) * r, y, Math.sin(theta) * r))
    }
    return directions
}

const createSphereRingsGeometry = () => {
    const segments = 64
    const positions: number[] = []
    const point = (axis: number, angle: number): [number, number, number] => {
        const c = Math.cos(angle)
        const s = Math.sin(angle)
        if (axis === 0) return [0, c, s]
        if (axis === 1) return [c, 0, s]
        return [c, s, 0]
    }
    for (let axis = 0; axis < 3; axis++) {
        for (let i = 0; i < segments; i++) {
            positions.push(...point(axis, (i / segments) * Math.PI * 2), ...point(axis, ((i + 1) / segments) * Math.PI * 2))
        }
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
    return geometry
}

class ThreePointLightHelper extends ThreeObject<SceneNodes.PointLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private localMesh = new THREE.Group()
    private globalMesh = new THREE.Group()

    private proxyMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
    private proxyRings: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>
    private proxyRays: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>

    private standHelper: ThreeStandHelper

    private readonly numRays = 50
    private readonly rayDirections = fibonacciSphereDirections(this.numRays)

    constructor(pointLight: ThreePointLight, threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        this.proxyMesh = new THREE.Mesh(new THREE.SphereGeometry(), new THREE.MeshBasicMaterial({side: THREE.FrontSide, fog: false}))

        this.localMesh.add(this.proxyMesh)
        setThreeObjectPart(this.proxyMesh, pointLight)

        this.proxyRings = new THREE.LineSegments(
            createSphereRingsGeometry(),
            new THREE.LineBasicMaterial({color: new THREE.Color(0.73, 0.73, 0.73), fog: false}),
        )
        this.localMesh.add(this.proxyRings)
        setThreeObjectPart(this.proxyRings, pointLight)

        {
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute(this.numRays * 2 * 3, 3))
            this.proxyRays = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({color: 0x666666, fog: false}))
        }
        this.localMesh.add(this.proxyRays)

        this.standHelper = new ThreeStandHelper(threeSceneManagerService, onAsyncUpdate)

        this.globalMesh.add(this.standHelper.getRenderObject())
        setThreeObjectPart(this.standHelper.getRenderObject(), pointLight)

        this.renderObject.add(this.localMesh)
        this.renderObject.add(this.globalMesh)

        configThreeHelperObjectLayers(this.getRenderObject())
    }

    setup(sceneNode: SceneNodes.PointLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["color", "intensity"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({color, intensity}) => {
                    this.proxyMesh.material.color.copy(
                        clampColor01(new THREE.Color(...color).multiplyScalar(intensity * GlobalRenderConstants.lightIntensityScale)),
                    )
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.localMesh)
                },
            ),
            objectFieldsDifferent(sceneNode, this.parameters, ["radius"], undefined, ({radius}) => {
                updateTransform(new THREE.Matrix4().scale(new THREE.Vector3(radius, radius, radius)), this.proxyMesh)
                const ringRadius = Math.max(radius, MIN_GIZMO_RADIUS)
                updateTransform(new THREE.Matrix4().scale(new THREE.Vector3(ringRadius, ringRadius, ringRadius)), this.proxyRings)
            }),
            this.updateRays(sceneNode),
            this.standHelper.update(sceneNode),
            objectFieldsDifferent(sceneNode, this.parameters, ["visibleDirectly"], undefined, ({visibleDirectly}) => {
                const material = this.proxyMesh.material
                const hidden = !visibleDirectly
                material.transparent = hidden
                material.opacity = hidden ? 0 : 1
                material.depthWrite = !hidden
                material.needsUpdate = true

                configThreeHelperObjectLayers(this.proxyMesh, material.depthWrite ? HELPER_OBJECTS_LAYER : OVERLAY_OBJECTS_LAYER)
            }),
        ])
    }

    private updateRays(sceneNode: SceneNodes.PointLight) {
        return objectFieldsDifferent(sceneNode, this.parameters, ["radius", "intensity"], undefined, ({radius, intensity}) => {
            const buffer = this.proxyRays.geometry.getAttribute("position") as THREE.Float32BufferAttribute

            const start = Math.max(radius, MIN_GIZMO_RADIUS)
            const end = start + 2 * intensity
            for (let i = 0; i < this.numRays; i++) {
                const d = this.rayDirections[i]
                buffer.setXYZ(2 * i, d.x * start, d.y * start, d.z * start)
                buffer.setXYZ(2 * i + 1, d.x * end, d.y * end, d.z * end)
            }
            buffer.needsUpdate = true
        })
    }

    override dispose() {
        this.proxyMesh.geometry.dispose()
        this.proxyMesh.material.dispose()
        this.proxyRings.geometry.dispose()
        this.proxyRings.material.dispose()
        this.proxyRays.geometry.dispose()
        this.proxyRays.material.dispose()

        this.standHelper.dispose()
    }
}
